import { useMemo, useState } from "react";
import { Select } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEntityLabelDropdown } from "@/hooks/entities/useEntitiesQueries";
import styles from "./AssignmentsAssistant.module.scss";

const SEARCH_DEBOUNCE_MS = 300;

interface CompanyPickerProps {
  value?: string;
  onChange: (tenantId: string | undefined) => void;
  /** A company picked for the person (the one their question named), with its name. */
  known?: { id: string; name: string };
  disabled?: boolean;
}

export function CompanyPicker({ value, onChange, known, disabled }: CompanyPickerProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const { data, isFetching } = useEntityLabelDropdown(true, debouncedSearch);
  // Keeps the picked company listed when a later search no longer returns it.
  const [picked, setPicked] = useState<{ value: string; label: string }>();

  const options = useMemo(() => {
    const fetched = (data?.data ?? []).map((option) => ({
      value: option.value,
      label: option.label,
    }));
    const current =
      picked?.value === value
        ? picked
        : known && known.id === value
          ? { value: known.id, label: known.name }
          : undefined;
    if (current && !fetched.some((o) => o.value === value)) {
      fetched.unshift(current);
    }
    return fetched;
  }, [data, picked, known, value]);

  return (
    <div className={styles.companyPicker}>
      <Select
        label="Company"
        placeholder="Search a company"
        size="sm"
        searchable
        clearable
        data={options}
        value={value ?? null}
        onChange={(next, option) => {
          setPicked(next ? { value: next, label: option.label } : undefined);
          onChange(next ?? undefined);
        }}
        searchValue={search}
        onSearchChange={setSearch}
        nothingFoundMessage={isFetching ? "Searching.." : "No company found"}
        disabled={disabled}
      />
    </div>
  );
}
